import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Chip,
  Stack,
  Avatar,
  CircularProgress,
  Alert,
  Grid,
  Button,
  Card,
  CardHeader,
  CardContent,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ImageList,
  ImageListItem,
  Paper,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import { 
  School as SchoolIcon,
  NavigateNext,
  ExpandMore,
  LocationOn,
  Language,
  Public,
  People,
  Person,
  CalendarToday,
  Star,
  EmojiEvents,
  Book,
  VideoLibrary,
  Image,
  Description,
  Map,
  OpenInNew,
  Share,
  Bookmark,
  PlayArrow,
  Close
} from '@mui/icons-material';
import { universityService, universityDataMapper } from '../services/universityService';
import ShareButtonAdvanced from '../components/common/ShareButtonAdvanced';
import BookmarkButton from '../components/common/BookmarkButton';

const UniversityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [university, setUniversity] = useState(null);
  const [faculties, setFaculties] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);

  const handleViewProgram = (programId) => {
    navigate(`/programs/${programId}`);
  };

  const handleViewFaculty = (faculty) => {
    // 使用 university/faculties/faculty 格式，确保唯一性
    // 如果大学数据还未加载，暂时使用 id 作为备选
    const universitySlug = university?.name_en_concat || 'university';
    const facultySlug = faculty.name_en_concat || faculty.id;
    navigate(`/universities/${universitySlug}/faculties/${facultySlug}`);
  };

  const handleOpenMap = () => {
    if (university?.mapLinks?.google) {
      window.open(university.mapLinks.google, '_blank');
    }
  };

  // 移除原来的 handleShare 函数，使用 ShareButtonAdvanced 组件

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setImageDialogOpen(true);
  };

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    setVideoDialogOpen(true);
  };

  useEffect(() => {
    const loadUniversityData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const [universityRes, programsRes, newsRes] = await Promise.all([
          universityService.getUniversityDetail(id),
          universityService.getUniversityPrograms(id, { size: 20 }),
          universityService.getUniversityNews(id, { size: 10 })
        ]);

        const universityData = universityRes.data?.data || universityRes.data;
        const mappedUniversity = universityDataMapper.mapUniversityFromApi(universityData);
        setUniversity(mappedUniversity);

        // 优先使用大学详情中的院系列表（包含 name_en_concat），为空时再回退到院系接口
        const facultiesDataFromDetail = (universityData && Array.isArray(universityData.faculties)) ? universityData.faculties : [];
        const facultiesData = facultiesDataFromDetail;
        const mappedFaculties = facultiesData.map(faculty => 
          universityDataMapper.mapFacultyFromApi(faculty)
        );
        setFaculties(mappedFaculties);

        const programsData = programsRes.data?.data || programsRes.data || [];
        const mappedPrograms = programsData.map(program => 
          universityDataMapper.mapProgramFromApi(program)
        );
        setPrograms(mappedPrograms);

        const newsData = newsRes.data?.data || newsRes.data || [];
        setNews(newsData);

      } catch (err) {
        setError(err.message || '加载失败');
      } finally {
        setLoading(false);
      }
    };

    loadUniversityData();
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', py: 8, textAlign: 'center' }}>
        <Container maxWidth="xl">
          <CircularProgress size={60} />
          <Typography sx={{ mt: 2 }}>正在加载大学详情...</Typography>
        </Container>
      </Box>
    );
  }

  if (error) {
  return (
      <Box sx={{ width: '100%', py: 8, textAlign: 'center' }}>
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Typography>无法加载大学信息，请稍后重试。</Typography>
        </Container>
      </Box>
    );
  }

  if (!university) {
    return null;
  }

  // 统计卡片组件
  const StatCard = ({ icon: Icon, title, value, color = "primary", subtitle }) => (
    <Card sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
      <CardContent sx={{ p: 3, textAlign: 'center' }}>
        <Box
          sx={{
            bgcolor: `${color}.main`,
            color: 'white',
            borderRadius: '50%',
            width: 60,
            height: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2
          }}
        >
          <Icon sx={{ fontSize: 28 }} />
        </Box>
        <Typography variant="h6" fontWeight={700} color="text.primary" gutterBottom>
          {value || '-'}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ width: '100%', py: 4, bgcolor: 'grey.50', minHeight: '100vh' }}>
      <Container maxWidth="xl">
        {/* 面包屑导航 */}
        <Breadcrumbs 
          separator={<NavigateNext fontSize="small" />} 
          sx={{ mb: 3 }}
          aria-label="breadcrumb"
        >
          <Link color="inherit" href="/" underline="hover">
            首页
          </Link>
          <Link color="inherit" href="/universities" underline="hover">
            院校库
          </Link>
          <Typography color="text.primary">{university.name || '大学详情'}</Typography>
        </Breadcrumbs>

        {/* 大学基本信息头部 */}
        <Card sx={{ mb: 4, position: 'relative', overflow: 'visible' }}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 120,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px 12px 0 0',
              zIndex: 0
            }}
          />
          <CardContent sx={{ pt: 8, pb: 4, position: 'relative', zIndex: 1 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3, position: 'relative', zIndex: 2 }}>
                  <Chip 
                    label={university.type} 
                    color={university.isPublic ? "primary" : "secondary"} 
                    variant="filled"
                    sx={{ fontWeight: 600 }}
                  />
                  {university.ranking.global !== '-' && (
                    <Chip 
                      label={`世界排名 #${university.ranking.global}`} 
                      variant="outlined"
                      sx={{ 
                        fontWeight: 600,
                        borderColor: 'rgba(255,255,255,0.3)',
                        color: 'white',
                        '&:hover': {
                          borderColor: 'rgba(255,255,255,0.5)',
                          backgroundColor: 'rgba(255,255,255,0.1)'
                        }
                      }}
                    />
                  )}
                  {university.ranking.asia !== '-' && (
                    <Chip 
                      label={`亚洲排名 #${university.ranking.asia}`} 
                      variant="outlined"
                      sx={{ 
                        fontWeight: 600,
                        borderColor: 'rgba(255,255,255,0.3)',
                        color: 'white',
                        '&:hover': {
                          borderColor: 'rgba(255,255,255,0.5)',
                          backgroundColor: 'rgba(255,255,255,0.1)'
                        }
                      }}
                    />
                  )}
                </Stack>
                <Typography variant="h4" fontWeight={700} color="text.primary" gutterBottom sx={{ position: 'relative', zIndex: 2, lineHeight: 1.3 }}>
                  {university.name || '大学名称加载中...'}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3, position: 'relative', zIndex: 2, lineHeight: 1.4 }}>
                  {university.nameEn || 'English Name Loading...'}
                </Typography>
                {university.shorterForm && (
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3, position: 'relative', zIndex: 2 }}>
                    简称：{university.shorterForm}
                  </Typography>
                )}
                <Stack direction="row" spacing={3} alignItems="center" sx={{ position: 'relative', zIndex: 2, mt: 2 }}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: 200,
                      height: 80,
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: 'white',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '3px solid rgba(255,255,255,0.2)',
                      flexShrink: 0
                    }}
                  >
                    {university.logo ? (
                      <img
                        src={university.logo}
                        alt={university.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          padding: '8px'
                        }}
                      />
                    ) : (
                      <SchoolIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.3, mb: 1 }}>
                      {university.nameEn || university.name}
                    </Typography>
                    {university.nameEn && (
                      <Typography variant="subtitle1" color="text.secondary" sx={{ lineHeight: 1.4, mb: 0.5 }}>
                        {university.name}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                      {university.type} • 成立于 {university.established} 年
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1}>
                    <BookmarkButton
                      itemId={university.id}
                      itemType="university"
                      title={university.name}
                      description={`查看 ${university.name} 的详细信息`}
                      image={university.logo}
                      variant="outlined"
                      fullWidth
                      onBookmarkChange={(action, data) => {
                        // 这里可以添加收藏统计逻辑
                      }}
                    />
                    <ShareButtonAdvanced
                      title={university.name || '大学详情'}
                      url={window.location.href}
                      description={`查看 ${university.name} 的详细信息`}
                      image={university.logo}
                      hashtags={['大学', '教育', university.type, university.name]}
                      platforms={['wechat', 'weibo', 'qq', 'qzone', 'xiaohongshu', 'douyin', 'copy']}
                      showQRCode={true}
                      showPrint={true}
                      showDownload={false}
                      variant="outlined"
                      fullWidth
                      onShare={(platform, data) => {
                        // 这里可以添加分享统计逻辑
                      }}
                    />
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 主要内容区域 */}
        <Grid container spacing={4}>
          {/* 左侧内容 */}
          <Grid item xs={12} lg={8}>
            {/* 标签页导航 */}
            <Card sx={{ mb: 4 }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="基本信息" icon={<SchoolIcon />} iconPosition="start" />
                <Tab label="院系设置" icon={<Book />} iconPosition="start" />
                <Tab label="校园风光" icon={<Image />} iconPosition="start" />
                <Tab label="视频介绍" icon={<VideoLibrary />} iconPosition="start" />
                <Tab label="最新资讯" icon={<Description />} iconPosition="start" />
              </Tabs>

              {/* 基本信息标签页 */}
              {activeTab === 0 && (
                <CardContent sx={{ p: 4 }}>
                  <Grid container spacing={4}>
                    {/* 学校介绍 */}
                    {university.content.intro && university.content.intro.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          学校介绍
                        </Typography>
                        {university.content.intro.map((intro, index) => (
                          <Typography
                            key={index}
                            variant="body1"
                            sx={{ mb: 2, lineHeight: 1.7 }}
                            dangerouslySetInnerHTML={{ __html: intro.content || '' }}
                          />
                        ))}
                      </Grid>
                    )}

                    {/* 学校优势 */}
                    {university.content.advantages && university.content.advantages.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          学校优势
                        </Typography>
                        <List>
                          {university.content.advantages.map((advantage, index) => (
                            <ListItem key={index} sx={{ px: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{ m: 0 }}
                                component="div"
                                dangerouslySetInnerHTML={{ __html: advantage.content || '' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    )}

                    {/* 领导团队 */}
                    <Grid item xs={12}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        领导团队
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary">
                              校长
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {university.leadership.principal}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary">
                              副校长
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {university.leadership.vicePrincipal}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              )}

              {/* 院系设置标签页 */}
              {activeTab === 1 && (
                <CardContent sx={{ p: 4 }}>
                  <Grid container spacing={4}>
                    {/* 院系列表 */}
                    <Grid item xs={12}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        院系设置 ({faculties.length})
                      </Typography>
                      <Grid container spacing={2}>
                        {faculties.map((faculty) => (
                          <Grid item xs={12} sm={6} md={4} key={faculty.id}>
                            <Card 
                              sx={{ 
                                height: '100%', 
                                cursor: 'pointer',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-2px)' }
                              }}
                              onClick={() => handleViewFaculty(faculty)}
                            >
                              <CardContent>
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                  {faculty.nameEn || faculty.name}
                                </Typography>
                                {faculty.nameEn && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {faculty.name}
                                  </Typography>
                                )}
                                <Stack direction="row" spacing={2}>
                                  <Typography variant="caption" color="text.secondary">
                                    教师: {faculty.teachers}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    学生: {faculty.students}
                                  </Typography>
                                </Stack>
                                {faculty.director !== '-' && (
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                    院长: {faculty.director}
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>

                    {/* 热门专业列表 - 暂时注释掉，后续可能需要恢复 */}
                    {/* <Divider sx={{ my: 3 }} />

                    <Grid item xs={12}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        热门专业 ({programs.length})
                      </Typography>
                      {programs.length > 0 ? (
                        <Grid container spacing={2}>
                          {programs.map((program) => (
                            <Grid item xs={12} sm={6} md={4} key={program.id}>
                              <Card 
                                sx={{ 
                                  height: '100%', 
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s',
                                  '&:hover': { transform: 'translateY(-2px)' }
                                }}
                                onClick={() => handleViewProgram(program.id)}
                              >
                                <CardContent>
                                  <Typography variant="h6" fontWeight={600} gutterBottom>
                                    {program.name_en || program.nameEn || program.name_cn || program.name || '专业名称'}
                                  </Typography>
                                  {(program.name_en || program.nameEn) && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                      {program.name_cn || program.name}
                                    </Typography>
                                  )}
                                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                    <Chip 
                                      label={program.academic_degree || program.level || '学位'} 
                                      size="small" 
                                      color="primary" 
                                    />
                                    <Chip 
                                      label={program.mode || '课程型'} 
                                      size="small" 
                                      variant="outlined" 
                                    />
                                  </Stack>
                                  <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="caption" color="text.secondary">
                                      学制: {program.duration || '-'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      学费: {program.currency || 'MYR'} {program.fees_total || program.totalFee || '-'}
                                    </Typography>
                                  </Stack>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                          暂无专业信息
                        </Typography>
                      )}
                    </Grid> */}
                  </Grid>
                </CardContent>
              )}

              {/* 校园风光标签页 */}
              {activeTab === 2 && (
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    校园风光 ({university.media.images.length})
                  </Typography>
                  {university.media.images.length > 0 ? (
                    <ImageList variant="masonry" cols={3} gap={8}>
                      {university.media.images.map((image, index) => (
                        <ImageListItem key={index}>
                          <img
                            src={universityDataMapper.buildImageUrl(image.path)}
                            alt={image.title || `校园图片 ${index + 1}`}
                            loading="lazy"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleImageClick(image)}
                          />
                        </ImageListItem>
                      ))}
                    </ImageList>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      暂无校园风光图片
                    </Typography>
                  )}
                </CardContent>
              )}

              {/* 视频介绍标签页 */}
              {activeTab === 3 && (
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    视频介绍 ({university.media.videos.length})
                  </Typography>
                  {university.media.videos.length > 0 ? (
                    <Grid container spacing={3}>
                      {university.media.videos.map((video, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Card 
                            sx={{ 
                              cursor: 'pointer',
                              transition: 'transform 0.2s',
                              '&:hover': { transform: 'translateY(-2px)' }
                            }}
                            onClick={() => handleVideoClick(video)}
                          >
                            <Box sx={{ position: 'relative' }}>
                              <img
                                src={universityDataMapper.buildImageUrl(video.thumbnail || video.path)}
                                alt={video.title || `视频 ${index + 1}`}
                                style={{ width: '100%', height: 200, objectFit: 'cover' }}
                              />
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  bgcolor: 'rgba(0,0,0,0.7)',
                                  borderRadius: '50%',
                                  p: 1
                                }}
                              >
                                <PlayArrow sx={{ color: 'white', fontSize: 40 }} />
                              </Box>
                            </Box>
                            <CardContent>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {video.title || `视频介绍 ${index + 1}`}
                              </Typography>
                              {video.description && (
                                <Typography variant="body2" color="text.secondary">
                                  {video.description}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      暂无视频介绍
                    </Typography>
                  )}
                </CardContent>
              )}

              {/* 文章内容标签页 */}
              {activeTab === 4 && (
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    最新资讯 ({news.length})
                  </Typography>
                  {news.length > 0 ? (
                    <Grid container spacing={3}>
                      {news.map((article) => (
                        <Grid item xs={12} md={6} key={article.id}>
                          <Card 
                            sx={{ 
                              height: '100%',
                              transition: 'transform 0.2s',
                              '&:hover': { transform: 'translateY(-2px)' }
                            }}
                          >
                            {article.cover_image && (
                              <Box
                                component="img"
                                sx={{
                                  height: 200,
                                  width: '100%',
                                  objectFit: 'cover',
                                }}
                                src={universityDataMapper.buildImageUrl(article.cover_image)}
                                alt={article.title}
                              />
                            )}
                            <CardContent>
                              <Typography variant="h6" fontWeight={600} gutterBottom>
                                {article.title}
                              </Typography>
                              {article.summary && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  {article.summary}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                {article.type} • {new Date(article.created_at).toLocaleDateString()}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      暂无新闻资讯
                    </Typography>
                  )}
                </CardContent>
              )}
            </Card>
          </Grid>

          {/* 右侧统计信息 */}
          <Grid item xs={12} lg={4}>
            <Stack spacing={3}>
              {/* 关键信息卡片 */}
              <Card>
                <CardHeader
                  title="关键信息"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <StatCard
                        icon={CalendarToday}
                        title="建校时间"
                        value={university.established}
                        color="primary"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <StatCard
                        icon={Public}
                        title="学校类型"
                        value={university.type}
                        color="secondary"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <StatCard
                        icon={EmojiEvents}
                        title="世界排名"
                        value={university.ranking.global !== '-' ? `#${university.ranking.global}` : '-'}
                        color="success"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <StatCard
                        icon={Star}
                        title="亚洲排名"
                        value={university.ranking.asia !== '-' ? `#${university.ranking.asia}` : '-'}
                        color="warning"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* 学生统计卡片 */}
              <Card>
                <CardHeader
                  title="学生统计"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                />
                <CardContent>
                  <Stack spacing={2}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>学生总数</Typography>
                      <Typography fontWeight={600}>
                        {university.students.total}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>本科生</Typography>
                      <Typography fontWeight={600}>
                        {university.students.undergraduate}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>研究生</Typography>
                      <Typography fontWeight={600}>
                        {university.students.postgraduate}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>国际学生</Typography>
                      <Typography fontWeight={600}>
                        {university.students.international}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>教师人数</Typography>
                      <Typography fontWeight={600}>
                        {university.teachers}
        </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* 院系统计卡片 */}
              <Card>
                <CardHeader
                  title="院系统计"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                />
                <CardContent>
                  <Stack spacing={2}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>院系数量</Typography>
                      <Typography fontWeight={600}>
                        {faculties.length}
        </Typography>
      </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>专业数量</Typography>
                      <Typography fontWeight={600}>
                        {programs.length}
      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* 联系方式卡片 - 暂时屏蔽 */}
              {/* <Card>
                <CardHeader
                  title="联系方式"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                />
                <CardContent>
                  <Stack spacing={2}>
                    {university.website && (
                      <Button 
                        variant="outlined" 
                        startIcon={<Language />}
                        fullWidth
                        href={university.website}
                        target="_blank"
                      >
                        官方网站
                      </Button>
                    )}
                    {university.mapLinks?.google && (
                      <Button 
                        variant="outlined" 
                        startIcon={<Map />}
                        fullWidth
                        onClick={handleOpenMap}
                      >
                        查看地图
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </Card> */}
            </Stack>
          </Grid>
        </Grid>
    </Container>

      {/* 图片查看对话框 */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedImage?.title || '校园图片'}
            </Typography>
            <IconButton onClick={() => setImageDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <img
              src={universityDataMapper.buildImageUrl(selectedImage.path)}
              alt={selectedImage.title || '校园图片'}
              style={{ width: '100%', height: 'auto' }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* 视频查看对话框 */}
      <Dialog
        open={videoDialogOpen}
        onClose={() => setVideoDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedVideo?.title || '视频介绍'}
            </Typography>
            <IconButton onClick={() => setVideoDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedVideo && (
            <Box sx={{ position: 'relative', width: '100%', height: 0, pb: '56.25%' }}>
              <iframe
                src={selectedVideo.path}
                title={selectedVideo.title || '视频介绍'}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                allowFullScreen
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UniversityDetail;
